import { useMemo } from 'react'

import { useQuery } from '@apollo/client'

import {
  GET_CURRENT_CUSTOMER_GIFT_CARDS,
  GET_CURRENT_CUSTOMER_ORDERS,
  GET_CURRENT_CUSTOMER_RESERVATIONS,
  GET_PROFILE_FEATURES,
} from 'Const'

import { ALL_PROFILE_TABS, ProfileTabKey } from '../TabNav'

interface Menu {
  deliverySettings: unknown[]
  onlineOrdering: boolean
  pickupSetting: { id: string } | null
}

interface Location {
  menus: Menu[]
  reservationAllowed: boolean
}

// Mirrors the Liquid ordering check: online ordering has to be on *and* the menu
// has to be fulfillable, otherwise nothing can actually be ordered from it.
const isOrderable = (menu: Menu): boolean =>
  menu.onlineOrdering && (menu.pickupSetting !== null || menu.deliverySettings.length > 0)

/**
 * A tab is shown when the feature is switched on, or when the customer has
 * history under it — restaurants toggle ordering and gift cards on and off, and
 * past orders shouldn't disappear with them.
 */
export const useVisibleProfileTabs = (signedIn: boolean): ProfileTabKey[] => {
  const skip = !signedIn
  const features = useQuery(GET_PROFILE_FEATURES, { skip })
  const orders = useQuery(GET_CURRENT_CUSTOMER_ORDERS, { skip })
  const reservations = useQuery(GET_CURRENT_CUSTOMER_RESERVATIONS, { skip })
  const giftCards = useQuery(GET_CURRENT_CUSTOMER_GIFT_CARDS, { skip })

  return useMemo(() => {
    // Show everything until we know better — hiding a tab we can't verify is
    // worse than briefly showing one that turns out to be off.
    if (skip || [features, orders, reservations, giftCards].some((query) => query.loading || query.error)) {
      return ALL_PROFILE_TABS
    }

    const shop = features.data?.currentShop
    const locations: Location[] = features.data?.currentAccount?.locations ?? []

    const hidden: ProfileTabKey[] = []

    const orderingEnabled = locations.some((location) => location.menus.some(isOrderable))
    if (!orderingEnabled && !orders.data?.currentCustomer?.orders?.length) hidden.push('orders')

    const reservationsEnabled = locations.some((location) => location.reservationAllowed)
    if (!reservationsEnabled && !reservations.data?.currentCustomer?.reservations?.length) hidden.push('reservations')

    const giftCardsEnabled = Boolean(shop?.isGiftCardAvailable) && !shop?.checkoutSetting?.hideGiftCards
    if (!giftCardsEnabled && !giftCards.data?.currentCustomer?.orderGiftCards?.length) hidden.push('giftCards')

    return ALL_PROFILE_TABS.filter((tab) => !hidden.includes(tab))
  }, [skip, features, orders, reservations, giftCards])
}
